<template>
  <v-container fluid class="list-page">
    <AssignStudent
      :editDialog="assignStudentDialog"
      :SelectedClass="selectedClass"
      @close="assignStudentClose"
      @submit="assigntStudentSubmit"
    />

    <div class="list-page-heading">
      <div>
        <div class="list-page-heading__eyebrow">Academics</div>

        <h1>Departments</h1>

        <p>
          Manage departments, classrooms, students, fees, and sponsorship
          requirements.
        </p>
      </div>

      <div class="list-page-heading__actions">
      <v-btn
        v-if="checkRightStatus(25)"
        color="primary"
        depressed
        @click="add"
      >
        <v-icon left>mdi-plus</v-icon>
        New department
      </v-btn>
      </div>
    </div>

    <v-row class="list-metrics">
      <v-col cols="6" sm="4" md="3">
        <div class="list-metric">
          <div class="list-metric__icon">
            <v-icon color="primary" size="21">
              mdi-school-outline
            </v-icon>
          </div>

          <div>
            <div class="list-metric__value">
              {{ payees.length }}
            </div>

            <div class="list-metric__label">
              Total departments
            </div>
          </div>
        </div>
      </v-col>

      <v-col cols="6" sm="4" md="3">
        <div class="list-metric">
          <div class="list-metric__icon">
            <v-icon color="primary" size="21">
              mdi-account-group-outline
            </v-icon>
          </div>

          <div>
            <div class="list-metric__value">
              {{ totalStudents }}
            </div>

            <div class="list-metric__label">
              Total students
            </div>
          </div>
        </div>
      </v-col>

      <v-col cols="6" sm="4" md="3">
        <div class="list-metric">
          <div class="list-metric__icon">
            <v-icon color="primary" size="21">
              mdi-cash-multiple
            </v-icon>
          </div>

          <div>
            <div class="list-metric__value">
              {{ totalExpectedFee | currency }}
            </div>

            <div class="list-metric__label">
              Expected monthly fees
            </div>
          </div>
        </div>
      </v-col>

      <v-col cols="6" sm="4" md="3">
        <div class="list-metric">
          <div class="list-metric__icon">
            <v-icon color="primary" size="21">
              mdi-hand-heart-outline
            </v-icon>
          </div>

          <div>
            <div
              class="list-metric__value"
              :class="{ 'sponsor-metric': totalSponsorNeed > 0 }"
            >
              {{ totalSponsorNeed | currency }}
            </div>

            <div class="list-metric__label">
              Total sponsor need
            </div>
          </div>
        </div>
      </v-col>
    </v-row>

    <v-card
      :loading="loading"
      class="list-card"
      elevation="0"
    >
      <div class="list-toolbar">
        <v-text-field
          v-model="search"
          class="list-toolbar__search"
          outlined
          prepend-inner-icon="mdi-magnify"
          dense
          background-color="#F8FAF8"
          placeholder="Search departments"
          single-line
          hide-details
          clearable
        />

        <div class="list-toolbar__actions">
        <v-btn
          icon
          outlined
          color="primary"
          :loading="loading"
          aria-label="Refresh departments"
          @click="getList"
        >
          <v-icon size="19">
            mdi-refresh
          </v-icon>
        </v-btn>
        </div>
      </div>

      <v-data-table
        v-if="$vuetify.breakpoint.mdAndUp"
        v-model="selected"
        :headers="headers"
        :items="payees"
        :search="search"
        :loading="loading"
        :sort-by="sortBy"
        :expanded.sync="expanded"
        :items-per-page="-1"
        hide-default-footer
        item-key="id"
        show-expand
        class="list-table departments-table"
      >
        <template v-slot:item="{ item, isExpanded, expand }">
          <tr class="list-table-row">
            <td class="expand-column">
              <v-btn
                icon
                small
                aria-label="Expand department"
                @click.stop="expand(!isExpanded)"
              >
                <v-icon>
                  {{
                    isExpanded
                      ? "mdi-chevron-up"
                      : "mdi-chevron-down"
                  }}
                </v-icon>
              </v-btn>
            </td>

            <td>
              <div class="list-identity">
                <div class="list-identity__icon">
                  <v-icon color="primary" size="20">
                    mdi-school-outline
                  </v-icon>
                </div>

                <div class="list-identity__content">
                  <div class="list-identity__title">
                    {{ item.name }}
                  </div>

                  <div class="list-identity__subtitle">
                    {{ item.studentCount || 0 }}
                    {{
                      Number(item.studentCount || 0) === 1
                        ? "student"
                        : "students"
                    }}
                  </div>
                </div>
              </div>
            </td>

            <td>
              <div class="list-value">
                <strong>
                  {{ item.defaultFee | currency }}
                </strong>

                <span>
                  Monthly fee
                </span>
              </div>
            </td>

            <td class="text-center">
              <span class="list-count-badge">
                {{ item.studentCount || 0 }}
              </span>
            </td>

            <td class="text-right">
              <div class="list-value text-right">
                <strong>
                  {{ getExpectedFee(item) | currency }}
                </strong>

                <span>
                  Expected
                </span>
              </div>
            </td>

            <td class="text-right">
              <div class="list-value text-right">
                <strong class="parent-pay">
                  {{ Number(item.totalParentPay || 0) | currency }}
                </strong>

                <span>
                  Parent paying
                </span>
              </div>
            </td>

            <td class="text-right">
              <div class="list-value text-right">
                <strong class="charity-pay">
                  {{ Number(item.totalCharity || 0) | currency }}
                </strong>

                <span>
                  Charity
                </span>
              </div>
            </td>

            <td class="text-right">
              <div class="list-value text-right">
                <strong
                  :class="{
                    'sponsor-needed': getSponsorNeed(item) > 0,
                    'sponsor-covered': getSponsorNeed(item) <= 0,
                  }"
                >
                  {{ getSponsorNeed(item) | currency }}
                </strong>

                <span>
                  Sponsor need
                </span>
              </div>
            </td>

            <td class="text-right">
              <v-menu bottom left offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    icon
                    aria-label="Department actions"
                  >
                    <v-icon>
                      mdi-dots-horizontal
                    </v-icon>
                  </v-btn>
                </template>

                <v-list dense class="menu-button">
                  <v-list-item @click="assignStudent(item)">
                    <v-list-item-icon>
                      <v-icon size="19">
                        mdi-account-plus-outline
                      </v-icon>
                    </v-list-item-icon>

                    <v-list-item-title>
                      Assign Student
                    </v-list-item-title>
                  </v-list-item>

                  <v-divider />

                  <v-list-item
                    v-if="checkRightStatus(27)"
                    @click="editClass(item)"
                  >
                    <v-list-item-icon>
                      <v-icon size="19">
                        mdi-pencil-outline
                      </v-icon>
                    </v-list-item-icon>

                    <v-list-item-title>
                      Edit
                    </v-list-item-title>
                  </v-list-item>

                  <v-list-item
                    v-if="checkRightStatus(28)"
                    @click="deletePayee(item)"
                  >
                    <v-list-item-icon>
                      <v-icon color="error" size="19">
                        mdi-delete-outline
                      </v-icon>
                    </v-list-item-icon>

                    <v-list-item-title class="error--text">
                      Delete
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </td>
          </tr>
        </template>

        <template v-slot:expanded-item="{ headers, item }">
          <td
            :colspan="headers.length"
            class="expanded-column pa-0"
          >
            <div class="department-expanded-content">
              <v-expansion-panels
                flat
                accordion
                class="department-expansion-panels"
              >
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <div class="expanded-panel-title">
                      <div class="expanded-panel-icon">
                        <v-icon color="primary" size="19">
                          mdi-door-open
                        </v-icon>
                      </div>

                      <div>
                        <strong>
                          Classrooms
                        </strong>

                        <span>
                          View and manage classrooms in {{ item.name }}
                        </span>
                      </div>
                    </div>
                  </v-expansion-panel-header>

                  <v-divider />

                  <v-expansion-panel-content>
                    <div class="embedded-list-wrap">
                      <ClassRoomList :ClassId="item.id" />
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <div class="expanded-panel-title">
                      <div class="expanded-panel-icon">
                        <v-icon color="primary" size="19">
                          mdi-account-group-outline
                        </v-icon>
                      </div>

                      <div>
                        <strong>
                          Students
                        </strong>

                        <span>
                          View students assigned to {{ item.name }}
                        </span>
                      </div>
                    </div>
                  </v-expansion-panel-header>

                  <v-divider />

                  <v-expansion-panel-content>
                    <div class="embedded-list-wrap">
                      <StudentList :ClassId="item.id" />
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>
          </td>
        </template>

        <template v-slot:no-data>
          <div class="list-empty-state">
            <v-icon size="44" color="grey lighten-1">
              mdi-school-outline
            </v-icon>

            <strong>
              No departments found
            </strong>

            <span>
              Add your first department to organize classrooms and students.
            </span>

            <v-btn
              v-if="checkRightStatus(25)"
              color="primary"
              depressed
              small
              @click="add"
            >
              <v-icon left small>
                mdi-plus
              </v-icon>

              Add department
            </v-btn>
          </div>
        </template>

        <template v-slot:no-results>
          <div class="list-empty-state">
            <v-icon size="44" color="grey lighten-1">
              mdi-magnify-close
            </v-icon>

            <strong>
              No matching departments
            </strong>

            <span>
              No departments matched "{{ search }}".
            </span>

            <v-btn
              text
              color="primary"
              small
              @click="search = null"
            >
              Clear search
            </v-btn>
          </div>
        </template>
      </v-data-table>

      <v-data-iterator
        v-else
        :items="payees"
        :search="search"
        :loading="loading"
        :items-per-page="-1"
        hide-default-footer
        class="list-mobile"
      >
        <template v-slot:default="{ items }">
          <div
            v-for="item in items"
            :key="item.id"
            class="list-mobile-card"
          >
            <div class="list-mobile-card__top">
              <div class="list-identity__icon">
                <v-icon color="primary" size="20">
                  mdi-school-outline
                </v-icon>
              </div>

              <div class="list-mobile-card__identity">
                <strong>
                  {{ item.name }}
                </strong>

                <span>
                  {{ item.studentCount || 0 }}
                  {{
                    Number(item.studentCount || 0) === 1
                      ? "student"
                      : "students"
                  }}
                </span>
              </div>

              <v-menu bottom left offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    icon
                    aria-label="Department actions"
                  >
                    <v-icon>
                      mdi-dots-horizontal
                    </v-icon>
                  </v-btn>
                </template>

                <v-list dense>
                  <v-list-item @click="assignStudent(item)">
                    <v-list-item-icon>
                      <v-icon size="19">
                        mdi-account-plus-outline
                      </v-icon>
                    </v-list-item-icon>

                    <v-list-item-title>
                      Assign Student
                    </v-list-item-title>
                  </v-list-item>

                  <v-list-item
                    v-if="checkRightStatus(27)"
                    @click="editClass(item)"
                  >
                    <v-list-item-icon>
                      <v-icon size="19">
                        mdi-pencil-outline
                      </v-icon>
                    </v-list-item-icon>

                    <v-list-item-title>
                      Edit
                    </v-list-item-title>
                  </v-list-item>

                  <v-list-item
                    v-if="checkRightStatus(28)"
                    @click="deletePayee(item)"
                  >
                    <v-list-item-icon>
                      <v-icon color="error" size="19">
                        mdi-delete-outline
                      </v-icon>
                    </v-list-item-icon>

                    <v-list-item-title class="error--text">
                      Delete
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>

            <div class="list-mobile-card__grid">
              <div>
                <span>
                  Monthly fee
                </span>

                <strong>
                  {{ item.defaultFee | currency }}
                </strong>
              </div>

              <div>
                <span>
                  Expected
                </span>

                <strong>
                  {{ getExpectedFee(item) | currency }}
                </strong>
              </div>

              <div>
                <span>
                  Parent paying
                </span>

                <strong class="parent-pay">
                  {{ Number(item.totalParentPay || 0) | currency }}
                </strong>
              </div>

              <div>
                <span>
                  Charity
                </span>

                <strong class="charity-pay">
                  {{ Number(item.totalCharity || 0) | currency }}
                </strong>
              </div>

              <div class="mobile-sponsor-value">
                <span>
                  Sponsor need
                </span>

                <strong
                  :class="{
                    'sponsor-needed': getSponsorNeed(item) > 0,
                    'sponsor-covered': getSponsorNeed(item) <= 0,
                  }"
                >
                  {{ getSponsorNeed(item) | currency }}
                </strong>
              </div>
            </div>

            <div class="mobile-expand-actions">
              <v-btn
                text
                color="primary"
                small
                @click="toggleMobileExpansion(item.id, 'classrooms')"
              >
                <v-icon left small>
                  mdi-door-open
                </v-icon>

                Classrooms
              </v-btn>

              <v-btn
                text
                color="primary"
                small
                @click="toggleMobileExpansion(item.id, 'students')"
              >
                <v-icon left small>
                  mdi-account-group-outline
                </v-icon>

                Students
              </v-btn>
            </div>

            <div
              v-if="
                mobileExpansion.id === item.id &&
                mobileExpansion.type === 'classrooms'
              "
              class="mobile-expanded-content"
            >
              <ClassRoomList :ClassId="item.id" />
            </div>

            <div
              v-if="
                mobileExpansion.id === item.id &&
                mobileExpansion.type === 'students'
              "
              class="mobile-expanded-content"
            >
              <StudentList :ClassId="item.id" />
            </div>
          </div>
        </template>

        <template v-slot:no-data>
          <div class="list-empty-state">
            <v-icon size="44" color="grey lighten-1">
              mdi-school-outline
            </v-icon>

            <strong>
              No departments found
            </strong>

            <span>
              Add your first department to organize classrooms and students.
            </span>
          </div>
        </template>
      </v-data-iterator>
    </v-card>
  </v-container>
</template>

<script>
import classService from "@/modules/Classes/service.js";
import { mapGetters } from "vuex";

const AssignStudent = () =>
  import("@/modules/Classes/components/AssignStudent.vue");

const ClassRoomList = () =>
  import("@/modules/ClassRoom/components/List.vue");

const StudentList = () =>
  import("@/modules/Student/components/List.vue");

export default {
  name: "class-list",

  components: {
    ClassRoomList,
    StudentList,
    AssignStudent,
  },

  data() {
    return {
      expanded: [],
      selected: [],
      mobileExpansion: {
        id: null,
        type: null,
      },
      createDialog: false,
      assignStudentDialog: false,
      selectedStudent: null,
      editDialog: false,
      selectedClass: null,

      headers: [
        {
          text: "",
          value: "data-table-expand",
          sortable: false,
          width: "60px",
        },
        {
          text: "Department",
          align: "left",
          value: "name",
          sortable: true,
        },
        {
          text: "Monthly fee",
          align: "left",
          value: "defaultFee",
          sortable: true,
          width: "140px",
        },
        {
          text: "Students",
          align: "center",
          value: "studentCount",
          sortable: true,
          width: "105px",
        },
        {
          text: "Expected fees",
          align: "right",
          value: "expectedFee",
          sortable: false,
          width: "140px",
        },
        {
          text: "Parent paying",
          align: "right",
          value: "totalParentPay",
          sortable: true,
          width: "140px",
        },
        {
          text: "Charity",
          align: "right",
          value: "totalCharity",
          sortable: true,
          width: "125px",
        },
        {
          text: "Sponsor need",
          align: "right",
          value: "sponsorNeed",
          sortable: false,
          width: "140px",
        },
        {
          text: "",
          value: "action",
          sortable: false,
          width: "64px",
        },
      ],

      payees: [],
      search: null,
      loading: true,
      sortBy: ["name"],
    };
  },

  created() {
    this.getList();
    document.title = "taleem - Department List";
  },

  computed: {
    ...mapGetters("global", [
      "currentUser",
      "checkRightStatus",
    ]),

    totalStudents() {
      return this.payees.reduce((total, department) => {
        return total + Number(department.studentCount || 0);
      }, 0);
    },

    totalExpectedFee() {
      return this.payees.reduce((total, department) => {
        return total + this.getExpectedFee(department);
      }, 0);
    },

    totalSponsorNeed() {
      return this.payees.reduce((total, department) => {
        return total + Math.max(this.getSponsorNeed(department), 0);
      }, 0);
    },
  },

  methods: {
    getExpectedFee(department) {
      return (
        Number(department.defaultFee || 0) *
        Number(department.studentCount || 0)
      );
    },

    getSponsorNeed(department) {
      return (
        this.getExpectedFee(department) -
        Number(department.totalParentPay || 0) -
        Number(department.totalCharity || 0)
      );
    },

    toggleMobileExpansion(id, type) {
      if (
        this.mobileExpansion.id === id &&
        this.mobileExpansion.type === type
      ) {
        this.mobileExpansion = {
          id: null,
          type: null,
        };

        return;
      }

      this.mobileExpansion = {
        id,
        type,
      };
    },

    assigntStudentSubmit() {
      this.assignStudentDialog = false;
      this.getList();
    },

    assignStudent(department) {
      this.selectedClass = department;
      this.assignStudentDialog = true;
    },

    assignStudentClose() {
      this.selectedStudent = null;
      this.selectedClass = null;
      this.assignStudentDialog = false;
    },

    add() {
      this.$router.push({
        path: "/departments/create",
      });
    },

    getList() {
      this.loading = true;
      this.editDialog = false;
      this.createDialog = false;

      return classService
        .getAll()
        .then((response) => {
          this.payees = response.data || [];
          this.loading = false;

          return response;
        })
        .catch((error) => {
          this.loading = false;

          this.$swal({
            title: "Department List",
            text:
              error &&
              error.data &&
              error.data.message
                ? error.data.message
                : "Unable to load the department list.",
            icon: "error",
          });
        });
    },

    editClass(department) {
      this.$router.push({
        path: `/departments/${department.id}/edit`,
      });
    },

    deletePayee(department) {
      this.$swal({
        title: "Are you sure?",
        text: `You want to delete ${department.name}. This action cannot be reverted.`,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#B71C1C",
        cancelButtonColor: "#43A047",
        cancelButtonText: "No",
        confirmButtonText: "Yes",
      }).then((result) => {
        if (result.value) {
          classService
            .delete(department.id)
            .then((response) => {
              if (response.data) {
                this.$swal(
                  "Deleted!",
                  "Department has been deleted.",
                  "success"
                );

                this.getList();
              }
            })
            .catch((error) => {
              this.$swal({
                title: "Delete Department",
                text:
                  error &&
                  error.data &&
                  error.data.message
                    ? error.data.message
                    : "Unable to delete this department.",
                icon: "error",
              });
            });
        }
      });
    },
  },
};
</script>

<style scoped>
/* Only page-specific styles remain here. Shared list styles live in app.scss. */

.sponsor-metric,
.sponsor-needed {
  color: #c62828 !important;
}

.sponsor-covered,
.parent-pay {
  color: #2e7d32 !important;
}

.charity-pay {
  color: #546e7a !important;
}

.expand-column {
  width: 60px;
  padding-right: 0 !important;
}

.expanded-column {
  background: #f7faf7;
}

.department-expanded-content {
  padding: 14px;
  border-bottom: 1px solid #e3e9e3;
}

.department-expansion-panels {
  background: transparent;
}

.department-expansion-panels ::v-deep .v-expansion-panel {
  margin-bottom: 10px;
  overflow: hidden;
  border: 1px solid #dfe7df;
  border-radius: 10px !important;
  background: white;
}

.department-expansion-panels ::v-deep .v-expansion-panel::before {
  box-shadow: none;
}

.department-expansion-panels ::v-deep .v-expansion-panel-header {
  min-height: 64px;
  padding: 12px 18px;
}

.expanded-panel-title {
  display: flex;
  align-items: center;
  gap: 11px;
}

.expanded-panel-icon {
  display: flex;
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  align-items: center;
  justify-content: center;
  border-radius: 9px;
  background: #eaf6eb;
}

.expanded-panel-title > div:last-child {
  display: flex;
  flex-direction: column;
}

.expanded-panel-title strong {
  color: #2e3d31;
  font-size: 13px;
}

.expanded-panel-title span {
  margin-top: 2px;
  color: #899489;
  font-size: 10px;
}

.embedded-list-wrap {
  padding: 4px 0;
}

.embedded-list-wrap ::v-deep .list-page {
  padding: 0;
  background: white;
}

.mobile-sponsor-value {
  grid-column: 1 / -1;
}

.mobile-expand-actions {
  display: flex;
  gap: 8px;
  margin: 13px 0 0 43px;
}

.mobile-expand-actions .v-btn {
  border-radius: 7px;
  text-transform: none;
  letter-spacing: 0;
}

.mobile-expanded-content {
  margin-top: 12px;
  padding: 10px;
  border: 1px solid #e3e9e3;
  border-radius: 10px;
  background: #f8faf8;
}

@media (max-width: 1200px) {
  .departments-table {
    overflow-x: auto;
  }

  .departments-table ::v-deep table {
    min-width: 1120px;
  }
}
</style>
